import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  BuildingOfficeIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { roomAPI } from '../services/api';
import { RoomWithStats, RoomCreateRequest, RoomUpdateRequest } from '../types/booking';
import Card from './ui/Card';
import Button from './ui/Button';
import StatusBanner from './ui/StatusBanner';
import Modal from './ui/Modal';
import FormField, { Input, Checkbox } from './ui/FormField';
import { apiErrorMessage } from '../utils/apiError';

const RoomManagement: React.FC = () => {
  const [rooms, setRooms] = useState<RoomWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<RoomWithStats | null>(null);

  // Form state
  const [formData, setFormData] = useState<{
    name: string;
    capacity: number;
    room_type: string;
    is_active: boolean;
  }>({
    name: '',
    capacity: 1,
    room_type: 'studio',
    is_active: true
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchRooms();
  }, []);

  const fetchRooms = async () => {
    try {
      setLoading(true);
      const roomsData = await roomAPI.getAllRooms();
      setRooms(roomsData);
      setError(null);
    } catch (err) {
      setError('Failed to fetch rooms. Please try again.');
      console.error('Error fetching rooms:', err);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = 'Room name is required';
    } else if (formData.name.length > 50) {
      errors.name = 'Room name must be 50 characters or less';
    }

    if (formData.capacity < 1) {
      errors.capacity = 'Capacity must be at least 1';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      name: '',
      capacity: 1,
      room_type: 'studio',
      is_active: true
    });
    setFormErrors({});
  };

  const handleCreateRoom = async () => {
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      const createData: RoomCreateRequest = {
        name: formData.name.trim(),
        capacity: formData.capacity,
        room_type: formData.room_type,
        is_active: formData.is_active
      };

      await roomAPI.createRoom(createData);
      await fetchRooms();
      setShowCreateModal(false);
      resetForm();
    } catch (err: any) {
      if (err.response?.status === 400 && err.response?.data?.detail?.includes('already exists')) {
        setFormErrors({ name: 'Room name already exists' });
      } else {
        setError('Failed to create room. Please try again.');
      }
      console.error('Error creating room:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditRoom = async () => {
    if (!validateForm() || !selectedRoom) return;

    try {
      setSubmitting(true);
      const updateData: RoomUpdateRequest = {
        name: formData.name.trim(),
        capacity: formData.capacity,
        room_type: formData.room_type,
        is_active: formData.is_active
      };

      await roomAPI.updateRoom(selectedRoom.id, updateData);
      await fetchRooms();
      setShowEditModal(false);
      setSelectedRoom(null);
      resetForm();
    } catch (err: any) {
      if (err.response?.status === 400 && err.response?.data?.detail?.includes('already exists')) {
        setFormErrors({ name: 'Room name already exists' });
      } else {
        setError('Failed to update room. Please try again.');
      }
      console.error('Error updating room:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteRoom = async () => {
    if (!selectedRoom) return;

    try {
      setSubmitting(true);
      await roomAPI.deleteRoom(selectedRoom.id);
      await fetchRooms();
      setShowDeleteModal(false);
      setSelectedRoom(null);
    } catch (err: any) {
      if (err.response?.status === 400) {
        setError(`Cannot delete room: ${apiErrorMessage(err, 'Room has active bookings')}`);
      } else {
        setError('Failed to delete room. Please try again.');
      }
      console.error('Error deleting room:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const openEditModal = (room: RoomWithStats) => {
    setSelectedRoom(room);
    setFormData({
      name: room.name,
      capacity: room.capacity,
      room_type: room.room_type || 'studio',
      is_active: room.is_active
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  const openDeleteModal = (room: RoomWithStats) => {
    setSelectedRoom(room);
    setShowDeleteModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-clinic-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-clinic-dark font-body">Room Management</h1>
          <p className="text-clinic-muted mt-1">Manage clinic rooms and view utilization statistics</p>
        </div>
        <Button
          onClick={openCreateModal}
          leftIcon={<PlusIcon className="h-4 w-4" />}
        >
          Add Room
        </Button>
      </div>

      {/* Error Message */}
      {error && (
        <StatusBanner
          variant="error"
          title="Error"
          message={error}
          dismissible
          onDismiss={() => setError(null)}
        />
      )}

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-clinic-primary/20 rounded-full flex items-center justify-center">
              <BuildingOfficeIcon className="h-6 w-6 text-clinic-primary" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-clinic-muted">Total Rooms</p>
              <p className="text-2xl font-semibold text-clinic-dark">{rooms.length}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-clinic-primary/20 rounded-full flex items-center justify-center">
              <CheckCircleIcon className="h-6 w-6 text-clinic-primary" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-clinic-muted">Active Rooms</p>
              <p className="text-2xl font-semibold text-clinic-dark">
                {rooms.filter(r => r.is_active).length}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-clinic-accent/20 rounded-full flex items-center justify-center">
              <XCircleIcon className="h-6 w-6 text-clinic-accent" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-clinic-muted">Inactive Rooms</p>
              <p className="text-2xl font-semibold text-clinic-dark">
                {rooms.filter(r => !r.is_active).length}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-clinic-gold/20 rounded-full flex items-center justify-center">
              <BuildingOfficeIcon className="h-6 w-6 text-clinic-gold" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-clinic-muted">Total Capacity</p>
              <p className="text-2xl font-semibold text-clinic-dark">
                {rooms.reduce((sum, room) => sum + room.capacity, 0)}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Rooms Table */}
      <Card padding="none" className="overflow-hidden">
        <div className="px-6 py-4 border-b border-clinic-primary-lighter">
          <h3 className="text-lg font-medium text-clinic-dark">Rooms</h3>
        </div>

        {rooms.length === 0 ? (
          <div className="text-center py-12">
            <BuildingOfficeIcon className="mx-auto h-12 w-12 text-clinic-muted" />
            <h3 className="mt-2 text-sm font-medium text-clinic-dark">No rooms</h3>
            <p className="mt-1 text-sm text-clinic-muted">Get started by creating a new room.</p>
            <div className="mt-6">
              <Button
                onClick={openCreateModal}
                leftIcon={<PlusIcon className="h-4 w-4" />}
              >
                Add Room
              </Button>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-clinic-primary-lighter">
              <thead className="bg-clinic-cream/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">
                    Room
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">
                    Capacity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">
                    Bookings
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">
                    Utilization
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-clinic-muted uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-clinic-primary-lighter">
                {rooms.map((room) => (
                  <tr key={room.id} className="hover:bg-clinic-cream/30 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <BuildingOfficeIcon className="h-5 w-5 text-clinic-muted mr-3" />
                        <div>
                          <div className="text-sm font-medium text-clinic-dark">{room.name}</div>
                          <div className="text-sm text-clinic-muted">
                            {room.room_type === 'pod' ? 'Pod' : 'Studio'} #{
                              rooms.filter(r => r.room_type === room.room_type).findIndex(r => r.id === room.id) + 1
                            }
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-clinic-dark">
                      {room.capacity} people
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        room.is_active
                          ? 'bg-clinic-primary/20 text-clinic-primary'
                          : 'bg-clinic-accent/20 text-clinic-accent'
                      }`}>
                        {room.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-clinic-dark">
                      <div>
                        <div>{room.active_bookings} active</div>
                        <div className="text-clinic-muted">{room.total_bookings} total</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-clinic-dark">
                      {room.utilization_percentage}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => openEditModal(room)}
                          className="text-clinic-primary hover:text-clinic-dark p-1 rounded hover:bg-clinic-cream transition-colors"
                          title="Edit Room"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => openDeleteModal(room)}
                          className="text-clinic-accent hover:text-clinic-accent p-1 rounded hover:bg-clinic-cream transition-colors"
                          title="Delete Room"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Create Room Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          resetForm();
        }}
        title="Create New Room"
      >
        <div className="space-y-4">
          <FormField
            label="Room Name"
            htmlFor="name"
            error={formErrors.name}
            required
          >
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={Boolean(formErrors.name)}
              placeholder="e.g., Room A, Consultation Room 1"
            />
          </FormField>

          <FormField
            label="Capacity"
            htmlFor="capacity"
            error={formErrors.capacity}
            required
          >
            <Input
              type="number"
              id="capacity"
              min={1}
              value={formData.capacity}
              onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 1 })}
              error={Boolean(formErrors.capacity)}
              placeholder="Number of people"
            />
          </FormField>

          <FormField label="Type" htmlFor="room_type">
            <select
              id="room_type"
              value={formData.room_type}
              onChange={(e) => setFormData({ ...formData, room_type: e.target.value })}
              className="w-full px-3 py-2 border border-clinic-primary-light rounded-input focus:outline-none focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary text-clinic-dark"
            >
              <option value="studio">Studio (Meeting Room)</option>
              <option value="pod">Pod (Individual Workspace)</option>
            </select>
          </FormField>

          <Checkbox
            id="is_active"
            label="Room is active and available for booking"
            checked={formData.is_active}
            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
          />
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <Button
            variant="ghost"
            onClick={() => {
              setShowCreateModal(false);
              resetForm();
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateRoom}
            isLoading={submitting}
          >
            {submitting ? 'Creating...' : 'Create Room'}
          </Button>
        </div>
      </Modal>

      {/* Edit Room Modal */}
      <Modal
        isOpen={showEditModal && !!selectedRoom}
        onClose={() => {
          setShowEditModal(false);
          setSelectedRoom(null);
          resetForm();
        }}
        title="Edit Room"
      >
        <div className="space-y-4">
          <FormField
            label="Room Name"
            htmlFor="edit-name"
            error={formErrors.name}
            required
          >
            <Input
              id="edit-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={Boolean(formErrors.name)}
              placeholder="e.g., Room A, Consultation Room 1"
            />
          </FormField>

          <FormField
            label="Capacity"
            htmlFor="edit-capacity"
            error={formErrors.capacity}
            required
          >
            <Input
              type="number"
              id="edit-capacity"
              min={1}
              value={formData.capacity}
              onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 1 })}
              error={Boolean(formErrors.capacity)}
              placeholder="Number of people"
            />
          </FormField>

          <FormField label="Type" htmlFor="edit-room_type">
            <select
              id="edit-room_type"
              value={formData.room_type}
              onChange={(e) => setFormData({ ...formData, room_type: e.target.value })}
              className="w-full px-3 py-2 border border-clinic-primary-light rounded-input focus:outline-none focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary text-clinic-dark"
            >
              <option value="studio">Studio (Meeting Room)</option>
              <option value="pod">Pod (Individual Workspace)</option>
            </select>
          </FormField>

          <Checkbox
            id="edit-is_active"
            label="Room is active and available for booking"
            checked={formData.is_active}
            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
          />
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <Button
            variant="ghost"
            onClick={() => {
              setShowEditModal(false);
              setSelectedRoom(null);
              resetForm();
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEditRoom}
            isLoading={submitting}
          >
            {submitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </Modal>

      {/* Delete Room Modal */}
      <Modal
        isOpen={showDeleteModal && !!selectedRoom}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedRoom(null);
        }}
        title="Delete Room"
      >
        <p className="text-sm text-clinic-muted mb-6">
          Are you sure you want to delete <strong className="text-clinic-dark">{selectedRoom?.name}</strong>?
          This action cannot be undone. The room can only be deleted if it has no active bookings.
        </p>

        <div className="flex justify-end space-x-3">
          <Button
            variant="ghost"
            onClick={() => {
              setShowDeleteModal(false);
              setSelectedRoom(null);
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteRoom}
            isLoading={submitting}
          >
            {submitting ? 'Deleting...' : 'Delete Room'}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default RoomManagement;
